import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import supportArticle from "@faire/web-api/routes/support/articles/articleId";
import { formatExpectedShipDateRange } from "@faire/web/common/expectedShipDateRange";
import { FaireDate } from "@faire/web/common/FaireDate";
import { intlDate } from "@faire/web/common/localization/date-fns/intlDate";
import { MediumFormatting } from "@faire/web/common/localization/formatting";
import { Button } from "@faire/web/slate/Button";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

import { getSettingOwFImproveTermsTouchpointsPDP } from "@faire/retailer/settings/getSettingOwFImproveTermsTouchpointsPDP";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { ProductAddToCartState } from "@faire/retailer/views/product/ProductDetails/AddToCart/ProductAddToCartState";

interface IProps
  extends Pick<
    ProductAddToCartState,
    | "isOpenSizingVariant"
    | "openSizingShipDateRange"
    | "baseOrderByDate"
    | "openSizingAllSameDates"
    | "selectedOption"
  > {
  product: IProductPageResponse.IPdpProduct;
  isPreorderExcludedFromPromo?: boolean;
}

const FAIRE_PREORDER_SUPPORT_ARTICLE = supportArticle("360038557671");

export const PreorderMessage: React.FC<IProps> = ({
  isOpenSizingVariant,
  openSizingShipDateRange,
  baseOrderByDate,
  openSizingAllSameDates,
  selectedOption,
  product,
  isPreorderExcludedFromPromo,
}) => {
  let expectedShipDateRangeString: string | undefined = undefined;

  if (isOpenSizingVariant && openSizingShipDateRange) {
    expectedShipDateRangeString = formatExpectedShipDateRange(
      {
        expected_ship_date:
          openSizingShipDateRange.startDate.normalizedUtcTimestamp,
        expected_ship_window_days: openSizingShipDateRange.windowDays,
      },
      "MMM d"
    );
  } else if (selectedOption) {
    expectedShipDateRangeString = formatExpectedShipDateRange(
      selectedOption,
      "MMM d"
    );
  }

  if (!expectedShipDateRangeString && product.shipping_information) {
    // default to use product level ship date
    expectedShipDateRangeString = formatExpectedShipDateRange(
      product.shipping_information,
      "MMM d"
    );
  }

  if (!expectedShipDateRangeString) {
    // if after pulling the date range from the product doesn't work, show nothing.
    return null;
  }

  // we show orderBy/stopSellingAt date when not open sizing
  // OR when open sizing and all options have same orderBy date
  const showPreorderWithOrderByDate =
    baseOrderByDate &&
    (!isOpenSizingVariant || (isOpenSizingVariant && openSizingAllSameDates));

  if (getSettingOwFImproveTermsTouchpointsPDP()) {
    return (
      <PreorderMessagingWithTermsOptions
        showPreorderWithOrderByDate={showPreorderWithOrderByDate}
        expectedShipDateRangeString={expectedShipDateRangeString}
        baseOrderByDate={baseOrderByDate}
        isPreorderExcludedFromPromo={isPreorderExcludedFromPromo}
      />
    );
  }

  return (
    <PreorderMessageBox data-test-id="add-to-cart-preorder-message-box">
      <Typography>
        <>
          {showPreorderWithOrderByDate ? (
            <StrictLocalMsg
              defaultMessage="Preorder ships <MediumFormatting>{expectedShipDate}</MediumFormatting>. You’ll be charged when this preorder ships. All orders must be placed by {orderByDate}."
              description={{
                text: "Description about the preorder's shipment date. Includes the shipment date range, when the user will be charged.",
              }}
              values={{
                expectedShipDate: expectedShipDateRangeString,
                orderByDate:
                  intlDate(baseOrderByDate.normalizedUtcTimestamp, "MMM d") ??
                  "",
                MediumFormatting: MediumFormatting,
              }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="Preorder ships <MediumFormatting>{expectedShipDate}</MediumFormatting>. You’ll be charged when this preorder ships."
              description={{
                text: "Description about the preorder's shipment date. Includes the shipment date range, when the user will be charged",
              }}
              values={{
                expectedShipDate: expectedShipDateRangeString,
                MediumFormatting: MediumFormatting,
              }}
            />
          )}{" "}
          {isPreorderExcludedFromPromo ? (
            <>
              <StrictLocalMsg
                defaultMessage="This brand has chosen to exclude preorder items from their market special."
                description={{
                  text: "Disclaimer that the Fair Market promotion does not apply to this item due to brand restriction",
                }}
              />{" "}
            </>
          ) : null}
          <LearnMoreLink />
        </>
      </Typography>
    </PreorderMessageBox>
  );
};

const LearnMoreLink: React.FC = () => {
  return (
    <Button
      variant="tertiary"
      target="_blank"
      linkTo={FAIRE_PREORDER_SUPPORT_ARTICLE}
    >
      <StrictLocalMsg
        defaultMessage="Learn more"
        description={{
          text: "Link to support article",
        }}
      />
    </Button>
  );
};

const PreorderMessagingWithTermsOptions: React.FC<{
  showPreorderWithOrderByDate: boolean | undefined;
  expectedShipDateRangeString: string;
  baseOrderByDate: FaireDate | undefined;
  isPreorderExcludedFromPromo: boolean | undefined;
}> = ({
  showPreorderWithOrderByDate,
  expectedShipDateRangeString,
  baseOrderByDate,
  isPreorderExcludedFromPromo,
}) => {
  const { isRetailerOnNetTerms } = useRetailerStore(["isRetailerOnNetTerms"]);

  const noTermsMessages =
    baseOrderByDate && showPreorderWithOrderByDate ? (
      <StrictLocalMsg
        defaultMessage="Ships {expectedShipDate}. You’ll be charged when the order ships. Orders must be placed by {orderByDate}."
        description={{
          text: "Description about the preorder's shipment date for a retailer not on payment terms. Includes the shipment date range, when the user will be charged, and when the order must be place by.",
          img: "https://cdn.faire.com/fastly/ee9efac7541c886ecf853336a30f8101150f7268300212f7f70ab12efd2d9058.png",
        }}
        values={{
          expectedShipDate: expectedShipDateRangeString,
          orderByDate:
            intlDate(baseOrderByDate.normalizedUtcTimestamp, "MMM d") ?? "",
        }}
      />
    ) : (
      <StrictLocalMsg
        defaultMessage="Ships {expectedShipDate}. You’ll be charged when the order ships."
        description={{
          text: "Description about the preorder's shipment date for a retailer not on payment terms. Includes the shipment date range, when the user will be charged",
          img: "https://cdn.faire.com/fastly/8b7c452a56101fec1042331c562cb4faa45be16d5ce52ec0166bbbe20a4248db.png",
        }}
        values={{
          expectedShipDate: expectedShipDateRangeString,
        }}
      />
    );

  const termsMessages =
    baseOrderByDate && showPreorderWithOrderByDate ? (
      <StrictLocalMsg
        defaultMessage="Ships {expectedShipDate}. You’ll be charged 60 days after your order ships. Orders must be placed by {orderByDate}."
        description={{
          text: "Description about the preorder's shipment date for a retailer using 60-day payment terms. Includes the shipment date range, when the user will be charged, and when the order must be place by.",
          img: "https://cdn.faire.com/fastly/62124ff5cf038b0d00f12544f575faccdbf2e5aa2b4d03c8cd742ba28771bad6.png",
        }}
        values={{
          expectedShipDate: expectedShipDateRangeString,
          orderByDate:
            intlDate(baseOrderByDate.normalizedUtcTimestamp, "MMM d") ?? "",
        }}
      />
    ) : (
      <StrictLocalMsg
        defaultMessage="Ships {expectedShipDate}. You’ll be charged 60 days after your order ships."
        description={{
          text: "Description about the preorder's shipment date for a retailer using 60-day payment terms. Includes the shipment date range, when the user will be charged",
          img: "https://cdn.faire.com/fastly/db63eb3da15d139e68091a54e3e9268a07b5570fd0c4b6ae788d83305f0c9cb0.png",
        }}
        values={{
          expectedShipDate: expectedShipDateRangeString,
        }}
      />
    );

  return (
    <PreorderMessageBox data-test-id="add-to-cart-preorder-message-box">
      <Typography>
        <>
          {isRetailerOnNetTerms ? termsMessages : noTermsMessages}{" "}
          {isPreorderExcludedFromPromo ? (
            <>
              <StrictLocalMsg
                defaultMessage="This brand has chosen to exclude preorder items from their market special."
                description={{
                  text: "Disclaimer that the Fair Market promotion does not apply to this item due to brand restriction",
                }}
              />
            </>
          ) : null}
          <LearnMoreLink />
        </>
      </Typography>
    </PreorderMessageBox>
  );
};

const PreorderMessageBox = styled.div`
  margin-top: 16px;
  text-align: left;
  width: 100%;
`;
